<template>
  <div
    class="group relative flex transition-all items-center"
    :class="[{ containerBg: !light }, { 'bg-bg-base-bg2 rounded-12': light }, { ' active:bg-rackley-shades-250 rounded-full bg-rackley-shades-300': !light }]"
    @mouseenter="handleParentHover"
    @mouseleave="handleParentLeave"
  >
    <button
      class="h-10 w-10 relative z-11
      flex justify-center items-center disabled:cursor-not-allowed! disabled:opacity-75!"
      :class="[
        colorType === 'warning' ?
          'border-threshold-shades-900 text-threshold-shades-900 hover:bg-threshold-shades-900! hover:text-white! active:bg-spanish-orange! active:text-black-95 active:border-rackley-shades-600!' : '',
        colorType === 'error' ?
          'border-warning-shades-800 text-warning-shades-800 hover:border-rackley-shades-600! hover:bg-warning-shades-800! hover:text-white! active:border-rackley-shades-600! active:text-black-96! active:bg-warning-shades-600!'
          : isActive ? 'bg-primary-hover! text-white hover:bg-primary-at-8! active:bg-primary!'
            : '',
        { 'bg-bg-base-bg1': light },
        { [`rounded-${rounded}`]: rounded },
        { 'border rounded-full text-black-200 active:bg-primary  bg-rackley-shades-300 border-rackley-shades-600 hover:bg-rackley-shades-400 hover:text-white': !light },
        { 'cursor-default!': !mainButtonClickable },
      ]"
      :title="buttonTitle"
      :disabled="disabled || loading"
      data-test="button-with-menu-btn"
      @click="emit('buttonClick')"
      @mouseenter="handleButtonHover"
      @mouseleave="handleButtonLeave"
    >
      <slot name="icon">
        <wui-icon
          :name="loading ? 'spinner' : icon"
          class="w-4.5! h-4.5!"
        />
      </slot>
    </button>
    <slot name="content" />
    <div>
      <div
        v-if="withMenu"
        class="w-full tablet:z-11"
      >
        <div
          :class="{'flex': withMenu}"
        >
          <button
            ref="activatorRef"
            data-test="button-with-menu-activator"
            class="h-6 w-6 hover:text-black-95 shrink-0 rounded-full text-black-200
             active:text-black-400 flex items-center justify-center"
            :disabled="disabled"
            @click.stop="toggleMenu()"
          >
            <wui-icon
              name="select"
              class="w-4! h-4! transition-all"
              :class="{
                'rotate-180': !top && !isMenuOpen || top && isMenuOpen,
              }"
            />
          </button>
          <div
            v-if="slots.badge"
            class="cursor-pointer flex h-[20px] flex-basis min-w-[20px] items-center
             justify-center rounded-full border border-white text-1112 text-white absolute
              top-[-10px] right-[-6px] bg-sinopia"
            @click="toggleMenu()"
          >
            <slot name="badge" />
          </div>
        </div>
        <teleport :to="teleportTarget">
          <div
            v-if="isMenuOpen"
            ref="menuRef"
            data-test="button-with-menu-popup"
            class="fixed z-9999"
            :style="popupStyles"
            @click="handleMenuClick"
          >
            <slot />
          </div>
        </teleport>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { WuiIcon, IconName, Direction, DirectionType } from '@wui/common-library'
import { onBeforeUnmount, onMounted, ref, toRefs } from 'vue'

import { usePopupListeners } from '@/shared/composables/use-popup-listeners'
import { usePositionPopUpByDirection } from '@/shared/composables/use-position-popup'

/* eslint-disable vue/require-default-prop -- colorType omitted from defaults to satisfy exactOptionalPropertyTypes */
const props = withDefaults(defineProps<{
  icon: IconName,
  light?: boolean,
  loading?: boolean,
  rounded?: 24 | undefined,
  colorType?: 'warning' | 'error' | 'primary' | undefined,
  withMenu?: boolean,
  direction?: DirectionType,
  isActive?: boolean
  disabled?: boolean
  top?: boolean
  closeOnClickInside?: boolean
  buttonTitle?: string
  withMenuWithoutArray?: boolean
  mainButtonClickable?: boolean
}>(), {
  light: false,
  loading: false,
  rounded: 24,
  withMenu: false,
  direction: Direction.TOP_LEFT,
  isActive: false,
  disabled: false,
  top: false,
  closeOnClickInside: true,
  buttonTitle: '',
  withMenuWithoutArray: false,
  mainButtonClickable: true,
})

const {
  colorType,
  loading,
  icon,
  withMenu,
  direction,
  isActive,
  disabled,
  top,
  closeOnClickInside,
  buttonTitle,
} = toRefs(props)

const emit = defineEmits<{
  (event: 'buttonClick'): void
}>()

const containerBg = ref('')
const isMenuOpen = ref(false)
const activatorRef = ref<HTMLElement | null>(null)
const menuRef = ref<HTMLElement | null>(null)
const teleportTarget = ref<HTMLElement | string>('body')

const updateTeleportTarget = () => {
  teleportTarget.value = document.fullscreenElement as HTMLElement ?? document.body
}

onMounted(() => {
  updateTeleportTarget()
  document.addEventListener('fullscreenchange', updateTeleportTarget)
})

onBeforeUnmount(() => {
  document.removeEventListener('fullscreenchange', updateTeleportTarget)
})

const { popupStyles, updatePosition } = usePositionPopUpByDirection({
  activator: activatorRef,
  popup: menuRef,
  direction,
})
const isButtonHovered = ref(false)
const isParentHovered = ref(false)

const closeMenu = () => {
  isMenuOpen.value = false
}

const toggleMenu = async () => {
  isMenuOpen.value = !isMenuOpen.value
  if (isMenuOpen.value) {
    await updatePosition()
  }
}

const handleMenuClick = () => {
  if (!closeOnClickInside.value) {
    return
  }
  closeMenu()
}

usePopupListeners({
  isOpen: isMenuOpen,
  activator: activatorRef,
  popup: menuRef,
  closeOnClickInside,
  updatePosition,
  close: closeMenu,
})

const handleButtonHover = () => {
  isButtonHovered.value = true
  containerBg.value = 'bg-rackley-shades-300'
}

const handleButtonLeave = () => {
  isButtonHovered.value = false
  if (isParentHovered.value) {
    containerBg.value = 'bg-rackley-shades-400'
  }
}

const handleParentHover = () => {
  isParentHovered.value = true
  if (!isButtonHovered.value) {
    containerBg.value = 'bg-rackley-shades-400'
  }
}

const handleParentLeave = () => {
  isParentHovered.value = false
  containerBg.value = 'bg-rackley-shades-300'
}

const slots = defineSlots()
</script>
